import axios from "axios";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Status color mapping
export const STATUS_COLORS = {
    Pending: {
        backgroundColor: "#FEF3C7",
        color: "#B45309",
        borderColor: "#F59E0B",
    },
    Approved: {
        backgroundColor: "#D5F3D8",
        color: "#70AD69",
        borderColor: "#73dc13",
    },
    Rejected: {
        backgroundColor: "#FFBBBD",
        color: "#A74E4E",
        borderColor: "#fb6464",
    },
    Cancelled: {
        backgroundColor: "#FFBBBD",
        color: "#A74E4E",
        borderColor: "#fb6464",
    },
    Birthday: {
        backgroundColor: "#f7cabc85",
        color: "#c86646",
        borderColor: "#ed9a81"
    },
    Holiday: {
        backgroundColor: "#8B5CF61A",
        color: "#6D28D9",
        borderColor: "#8B5CF6",
    },
    Event: {
        backgroundColor: "#0EA5E91A",
        color: "#0369A1",
        borderColor: "#0EA5E9",
    },
    Anniversary: {
        backgroundColor: "#FFF7E6",
        color: "#D97706",
        borderColor: "#F59E0B",
    },
    GoogleEvent: {
        backgroundColor: "#E0F7FA",
        color: "#006064",
        borderColor: "#00ACC1",
    },
    MicrosoftEvent: {
        backgroundColor: '#96A0DE',
        color: '#323c7a',
        borderColor: '#323c7a'
    },
};

// Category list for event filtering
export const CATEGORYLIST = [
    { name: 'Holidays', color: '#8B5CF6', eventTypes: ['Holiday'] },
    { name: 'Events', color: '#0EA5E9', eventTypes: ['Event', 'GoogleEvent', 'MicrosoftEvent'] },
    { name: 'Anniversaries', color: '#F59E0B', eventTypes: ['Anniversary'] },
    { name: 'Birthdays', color: '#FF8660', eventTypes: ['Birthday'] },
    { name: 'Time-Off', color: '#6A7988', eventTypes: ['Leave'] },
];

// Holidays for 2025
export const HOLIDAYS_2025 = [
    { date: "2025-01-01", name: "New Year's Day", description: "Global celebration of the new year" },
    { date: "2025-04-18", name: "Good Friday", description: "Christian holiday commemorating the crucifixion of Jesus" },
    { date: "2025-04-20", name: "Easter Sunday", description: "Christian holiday celebrating the resurrection of Jesus" },
    { date: "2025-05-01", name: "International Workers' Day", description: "Celebration of laborers and the working class" },
    { date: "2025-07-04", name: "Independence Day (US)", description: "US national holiday celebrating independence" },
    { date: "2025-10-02", name: "Gandhi Jayanti", description: "Indian holiday honoring Mahatma Gandhi's birthday" },
    { date: "2025-11-27", name: "Thanksgiving (US)", description: "US holiday for giving thanks and feasting" },
    { date: "2025-12-25", name: "Christmas Day", description: "Christian holiday celebrating the birth of Jesus" },
    { date: "2025-12-26", name: "Boxing Day", description: "Day for giving gifts to service workers" },
];

// Fetch leaves from the API
export const fetchLeaves = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.get("/api/time-off-request/view", {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });
        const filteredLeaves = response.data?.filter(
            (leave) => leave.status === "Pending" || leave.status === "Approved"
        );
        return filteredLeaves;
    } catch (error) {
        throw new Error("Failed to fetch leave data");
    }
};

// Fetch events from the API
export const fetchEvents = async (token) => {
    if (!token) throw new Error("No token found in Redux state");
    try {
        const response = await axios.post(
            "/api/events/data",
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        throw new Error("Failed to fetch event data");
    }
};

// Check Google Calendar integration status
export const checkGoogleCalendarIntegration = async (token, email) => {
    if (!token || !email) throw new Error('Token or email missing');
    try {
        const response = await axios.post(
            '/api/google-calendar/validate-email',
            { email },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data.exists;
    } catch (error) {
        console.error('Error checking Google Calendar integration:', error);
        return false;
    }
};

// Check Google Calendar token validity
export const checkTokenValidity = async (token, user, setIsTokenChecking) => {
    if (!token || !user?._id || !user?.email) return false;
    setIsTokenChecking(true);
    try {
        const response = await axios.post(
            '/api/google-calendar/check-token',
            {
                userId: user._id,
                email: user.email,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data.isValid;
    } catch (error) {
        console.error('Error checking token validity:', error);
        return false;
    } finally {
        setIsTokenChecking(false);
    }
};

// Check Google Calendar enabled status
export const checkGoogleCalendarEnabledStatus = async (token, userId) => {
    if (!token || !userId) return false;
    try {
        const response = await axios.get(
            `/api/google-calendar/check-integration-status/${userId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data.data.user_google_calendar_enabled;
    } catch (error) {
        console.error("Error checking Google Calendar enabled status:", error);
        return false;
    }
};

// Check Microsoft Calendar integration status
export const checkMicrosoftCalendarIntegration = async (token, email) => {
    if (!token || !email) throw new Error('Token or email missing');
    try {
        const response = await axios.post(
            '/api/microsoft-calendar/validate-email',
            { email },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data.exists;
    } catch (error) {
        console.error('Error checking Microsoft Calendar integration:', error);
        return false;
    }
};

// Check Microsoft Calendar token validity
export const checkMicrosoftTokenValidity = async (
    token,
    user,
    setIsMicrosoftTokenChecking
) => {
    if (!token || !user?._id || !user?.email) return false;
    setIsMicrosoftTokenChecking(true);
    try {
        const response = await axios.post(
            "/api/microsoft-calendar/check-token",
            {
                userId: user._id,
                email: user.email,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data.isValid;
    } catch (error) {
        console.error("Error checking Microsoft token validity:", error);
        return false;
    } finally {
        setIsMicrosoftTokenChecking(false);
    }
};

// Check Microsoft Calendar enabled status
export const checkMicrosoftCalendarEnabledStatus = async (token, userId) => {
    if (!token || !userId) return false;
    try {
        const response = await axios.get(
            `/api/microsoft-calendar/check-integration-status/${userId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data.data.user_microsoft_calendar_enabled;
    } catch (error) {
        console.error("Error checking Microsoft Calendar enabled status:", error);
        return false;
    }
};

// Fetch Google Calendar events
export const fetchGoogleCalendarEvents = async (
    token,
    selectedUsers,
    setIsGoogleTokenInvalidModalOpen
) => {
    if (selectedUsers.length === 0) return [];
    const now = new Date();
    const timeMin = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();
    const timeMax = new Date(
        now.getFullYear() + 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();

    try {
        const response = await axios.post(
            "/api/get/google-calendar/events/multiple",
            {
                userIds: selectedUsers,
                timeMin,
                timeMax,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        console.log("selectedGoogleEvents error:", error.message);
        setIsGoogleTokenInvalidModalOpen(true);
        throw error;
    }
};

// Fetch Microsoft Calendar events
export const fetchMicrosoftCalendarEvents = async (
    token,
    selectedUsers,
    setIsMicrosoftTokenInvalidModalOpen
) => {
    if (selectedUsers.length === 0) return [];
    const now = new Date();
    const timeMin = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();
    const timeMax = new Date(
        now.getFullYear() + 1,
        now.getMonth(),
        now.getDate()
    ).toISOString();

    try {
        const response = await axios.post(
            "/api/get/microsoft-calendar/events/multiple",
            {
                userIds: selectedUsers,
                timeMin,
                timeMax,
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
        return response.data;
    } catch (error) {
        console.log("selectedMicrosoftEvents error:", error.message);
        setIsMicrosoftTokenInvalidModalOpen(true);
        throw error;
    }
};

// Group events by key for calendar display
export const groupEventsByKey = (events) => {
    const groupedEvents = {};

    events.forEach((event) => {
        const userName = event.title.split(" - ")[0] || event.title.split("'s")[0] || event.title || "Holiday";
        const eventType = event.eventType || (event.title.includes("Birthday") ? "Birthday" : event.title.includes("Holiday") ? "Holiday" : event.title.split(" - ")[1] || "Leave");
        const uniqueId = event.id || uuidv4();
        const groupKey = event.start
            ? `${moment(event.start).format("YYYY-MM-DD")}-${moment(event.end).format("YYYY-MM-DD")}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`
            : `${event.date}-${userName}-${eventType}-${event.startTime || 'no-start'}-${event.endTime || 'no-end'}-${uniqueId}`;

        if (!groupedEvents[groupKey]) {
            groupedEvents[groupKey] = [];
        }
        groupedEvents[groupKey].push(event);
    });

    const grouped = Object.keys(groupedEvents).map((key) => {
        const eventsInGroup = groupedEvents[key];
        const firstEvent = eventsInGroup[0];

        let start = firstEvent.start || new Date(firstEvent.date);
        let end = firstEvent.end || new Date(firstEvent.date);
        if (firstEvent.allDay) {
            start = moment.tz(start, global.config.getTimeZone() || 'UTC').startOf('day').toDate();
            end = moment.tz(end, global.config.getTimeZone() || 'UTC').startOf('day').toDate();
        }

        return {
            id: key,
            title: eventsInGroup.length > 1 ? `${firstEvent.title} (+${eventsInGroup.length - 1})` : firstEvent.title,
            start,
            end,
            backgroundColor: firstEvent.backgroundColor,
            textColor: firstEvent.color,
            borderColor: firstEvent.borderColor,
            allDay: firstEvent.allDay !== undefined ? firstEvent.allDay : !firstEvent.startTime,
            extendedProps: {
                allEvents: eventsInGroup,
                groupKey: key,
                profileImage: firstEvent.profileImage,
                description: firstEvent.description,
                status: firstEvent.status,
                user: firstEvent.user,
                typeOfLeave: firstEvent.typeOfLeave,
                issueDate: firstEvent.issueDate,
                totalDays: firstEvent.totalDays,
                startDate: firstEvent.startDate,
                endDate: firstEvent.endDate,
                startTime: firstEvent.startTime,
                endTime: firstEvent.endTime,
                note: firstEvent.note,
                leaveId: firstEvent.leaveId,
                eventType: firstEvent.eventType,
                eventName: firstEvent.eventName,
                eventLocation: firstEvent.eventLocation,
                eventOrganizer: firstEvent.eventOrganizer,
                eventOrganizerId: firstEvent.eventOrganizerId,
                eventAttendees: firstEvent.eventAttendees,
                eventBanner: firstEvent.eventBanner,
                eventIsVirtual: firstEvent.eventIsVirtual,
                eventMeetingLink: firstEvent.eventMeetingLink,
                repeatType: firstEvent.repeatType,
            },
        };
    });

    return grouped;
};